import { Component, DestroyRef, EventEmitter, HostListener, Input, OnInit, Output, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject, switchMap } from 'rxjs';
import { UploadFile } from 'src/app/file/model/file.model';
import { FormService } from 'src/app/form/form.service';
import { FormCreate, FormTable } from 'src/app/form/model/form.model';
import { NotificationType } from 'src/app/notifications/notification.model';
import { NotificationService } from 'src/app/notifications/notification.service';
import { formCategoryList } from 'src/app/utils/enums/enums/form/form-category.enum';
import { notify, notifyError } from '../../../utils/function/notification.util';

import { UnsavedChangesPopupComponent } from 'src/app/utils/popup/unsaved-changes-popup/unsaved-changes-popup.component';

@Component({
  selector: 'add-form-popup',
  templateUrl: './add-form-popup.component.html',
  styleUrl: './add-form-popup.component.scss',
  standalone: false
})
export class AddFormPopupComponent implements OnInit {

  @Input() isModalVisible = false;

  @Output() stateVisibleModal = new EventEmitter<boolean>();
  @Output() newCreateForm = new EventEmitter<FormTable>();

  readonly formCategoryList = formCategoryList;
  readonly clearUploadEventSubject = new Subject<void>();

  form!: FormGroup;
  @ViewChild(UnsavedChangesPopupComponent) unsavedChangesPopup?: UnsavedChangesPopupComponent;

  get hasPendingChanges(): boolean {
    return this.form.dirty || !!this.fileForm;
  }
  formIDExistenceError = false;

  private fileForm?: File;
  private destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private formService: FormService,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.initializeFormGroup();
    this.initializeSubscription();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isModalVisible) this.popupClose();
  }

  popupClose(saved:boolean = false): void {
    if (!saved && this.hasPendingChanges) {
      if (!this.unsavedChangesPopup) return;
      this.unsavedChangesPopup.hasUnsavedChanges = true;
      this.unsavedChangesPopup.confirmLeave().subscribe(leave => {
        if (leave) this.finishClose();
      });
      return;
    }
    this.finishClose();
  }

  private finishClose(): void {
    this.form.reset();
    this.formIDExistenceError = false;
    this.clearUploadEventSubject.next();
    this.fileForm = undefined;
    this.stateVisibleModal.emit(false);
  }

  setFileUploaded(event: UploadFile): void {
    this.fileForm = event.file;
  }

  isButtonValidate(): boolean {
    return this.fileForm === undefined || this.form.invalid;
  }

  onClickValidate(): void {
    if (this.form.invalid || !this.fileForm) return;

    this.formService.checkExistenceFormID(this.form.get('formID')?.value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((exists: boolean) => {
        this.formIDExistenceError = exists;
        if (!exists) this.createForm();
      });
  }

  // =============== Private ===============

  private initializeFormGroup(): void {
    this.form = this.fb.group({
      formID:      ['', Validators.required],
      name:        ['', Validators.required],
      category:    ['', Validators.required],
      description: ['', Validators.required]
    });
  }

  private initializeSubscription(): void {
    // Clear the existence error as soon as the user edits the Form ID
    this.form.get('formID')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formIDExistenceError = false);
  }

  /** Creates the form, then chains the file upload before notifying + closing. */
  private createForm(): void {
    let createdForm: FormTable;

    this.formService.createForm(this.buildFormCreate())
      .pipe(
        switchMap((form: FormTable) => {
          createdForm = form;
          // fileForm is guaranteed by the guard in onClickValidate()
          return this.formService.uploadFileToForm(form.uuid, this.fileForm as File);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.newCreateForm.emit(createdForm);
          notify(this.notificationService, NotificationType.SUCCESS, 'add-form-popup.notification.created');
          this.popupClose(true);
        },
        error: (err) => {
          notifyError(this.notificationService, NotificationType.ERROR, 'add-form-popup.notification.error', err);
        }
      });
  }

  private buildFormCreate(): FormCreate {
    return {
      formID: this.form.get('formID')?.value,
      name: this.form.get('name')?.value,
      category: this.form.get('category')?.value,
      description: this.form.get('description')?.value
    };
  }
}
